/**
 * The on-demand "who did this on Shopify" lookup.
 *
 * A Shopify-sourced ledger row's actor is always the webhook, because the
 * webhook carries no operator identity. Shopify's own events collection does
 * carry a human author, and this asks for it — one request per operator
 * button press, never on a render path.
 *
 * PARTIAL BY CONSTRUCTION, and every caller has to surface that: Shopify
 * records lifecycle actions (created / published / unpublished / status
 * changed) and emits NOTHING for a field edit, so a price or title change
 * can never be attributed through here. The backend route says the same in
 * its OpenAPI description; see `contexts/commerce/domain/shopify-events.ts`
 * for the three prod verifications behind it.
 */
import { http } from '@lib/api';

/** The three mirrored resources that have a Shopify subject to ask about. */
export const SHOPIFY_EVENT_RESOURCE = {
  PRODUCTS: 'products',
  ORDERS: 'orders',
  CUSTOMERS: 'customers',
} as const;

export type ShopifyEventResource =
  (typeof SHOPIFY_EVENT_RESOURCE)[keyof typeof SHOPIFY_EVENT_RESOURCE];

export interface ShopifyActorEvent {
  /** When SHOPIFY recorded it — a different clock from the ledger's. */
  occurred_at: string;
  /** Lifecycle action: create / status_changed / published / unpublished. */
  verb: string | null;
  /** Staff member display name, `"Shopify"` when platform-authored, or null. */
  actor: string | null;
  /**
   * Whether a human in the Shopify admin caused it.
   *
   * Load-bearing beyond the name: `actor` reads `"Shopify"` for both a
   * platform action and some app-driven ones, so this is what separates
   * "a person did this" from "the platform did this" without matching on a
   * magic string.
   */
  by_user: boolean | null;
  /** Human-readable sentence. Carries HTML links — render as text, never raw. */
  description: string | null;
}

export interface ShopifyActorEvents {
  shopify_id: string;
  events: ShopifyActorEvent[];
}

export interface ShopifyEventLookupParams {
  resource_type: ShopifyEventResource;
  resource_id: string;
}

/**
 * Whether this resource type has a Shopify subject at all.
 *
 * `fulfillment_jobs` does not — it is an Onfleet record — and the backend
 * rejects it at the query schema. Callers use this to decide whether to OFFER
 * the lookup, so an operator is never given a button that can only 400.
 */
export function isShopifyEventResource(resourceType: string): resourceType is ShopifyEventResource {
  return (Object.values(SHOPIFY_EVENT_RESOURCE) as string[]).includes(resourceType);
}

export const shopifyEventAPI = {
  async lookup(params: ShopifyEventLookupParams): Promise<ShopifyActorEvents> {
    return http.get<ShopifyActorEvents>('/api/shopify-events', { params });
  },
};
